import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { omit } from 'lodash';

import showLiveSupport from '@xanadu/ui-shared/utils/showLiveSupport';
import IconChat from '../../../../images/icons/misc/live-chat.svg';
import IconInfoCenter from '../../../../images/icons/misc/info-center.svg';
import IconTour from '../../../../images/icons/misc/icon-tour.svg';

import styles from './Bar.module.scss';

const Bar = ({
  multiplesAvailable,
  isLoggedIn,
  sidebarContent,
  sidebarVisible,
  widthSize,
  highlightedButton,
  toggleCustomerOnboardingPopup,
  handleButtonClick,
  items
}) => {
  let newItems = { ...items };

  if (!isLoggedIn) {
    newItems = omit(newItems, ['account']);
  } else {
    newItems = omit(newItems, ['settings']);
  }

  const showTourModeButton = widthSize >= 1200;

  const elements = Object.keys(newItems).map((key) => {
    const { icon, name, hook } = items[key];

    const buttonClasses = classNames({
      [styles.button]: true,
      [styles['button--active']]: key === sidebarContent && sidebarVisible,
      [styles['button--unavailable']]: key === 'multiples' && !multiplesAvailable,
      '.gtm_cta': true,
    });

    return (
      <div
        key={key}
        name={key}
        className={buttonClasses}
        data-hook={hook}
        role="button"
        onClick={() => handleButtonClick(key)}
      >
        {icon}
        <span className={styles.buttonText}>{name}</span>
      </div>
    );
  });

  const linkClass = classNames({
    [styles.link]: true,
    '.gtm_cta': true,
  });

  const helpCenterClass = classNames({
    [styles.liveInfo]: true,
    [styles.helpCenter]: true,
  });

  const tourModeClass = classNames({
    [styles.liveInfo]: true,
    [styles.tour]: true,
  });

  return (
    <div className={styles.main}>
      {elements}
      {showTourModeButton && (
        <div className={tourModeClass}>
          {highlightedButton === 'tour' && (
            <div className={styles.highlight}>
              <span className={styles.pointer} />
              <div><b>Take the Tour</b></div>
              <div>{'Click here if you would like to re-take the Matchbook tour, or watch our introductory videos.'}</div>
            </div>
          )}
          <button
            className={linkClass}
            onClick={() => {
              toggleCustomerOnboardingPopup({
                visible: true,
                firstTimeVisitor: false
              });
            }}
          >
            <IconTour
              data-driver="start-tour-button"
              className={`${styles.icon}`}
              width="24px"
              height="24px"
            />
            <div className={`${styles.text}`}>
              <span>Tour</span>
            </div>
          </button>
        </div>
      )}
      <div className={helpCenterClass}>
        <a
          href="https://matchbook.com/page/faqs/updatingaccdetails"
          role="button"
          tabIndex={0}
          className={linkClass}
          rel="noopener noreferrer"
          target="_blank"
        >
          <IconInfoCenter
            className={`${styles.icon}`}
            width="24px"
            height="24px"
          />
          <div className={`${styles.text}`}>
            <span>FAQs</span>
          </div>
        </a>
      </div>
      <div className={styles.liveInfo}>
        <a
          role="button"
          tabIndex={0}
          className={linkClass}
          onClick={showLiveSupport}
        >
          <IconChat
            className={`${styles.icon}`}
            width="24px"
            height="24px"
          />
          <div className={`${styles.text}`}>
            <span>Live Chat</span>
          </div>
        </a>
      </div>
    </div>
  );
};

Bar.propTypes = {
  isLoggedIn: PropTypes.bool.isRequired,
  multiplesAvailable: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.bool
  ]).isRequired,
  sidebarContent: PropTypes.string,
  highlightedButton: PropTypes.string,
  widthSize: PropTypes.number.isRequired,
  sidebarVisible: PropTypes.bool.isRequired,
  handleButtonClick: PropTypes.func.isRequired,
  toggleCustomerOnboardingPopup: PropTypes.func.isRequired,
  items: PropTypes.object.isRequired,
};

export default Bar;
